import { z } from "zod";
import type { GifAttachment } from "./schemas";

/*
 * End-to-end encrypted direct messages: wire types and validation.
 *
 * The server only ever stores public keys, a recovery-code-sealed backup of the
 * private keys, per-member wrapped conversation keys and message ciphertext.
 * All cryptography happens in the client (apps/web/src/lib/e2ee).
 */

/** Public half of a P-256 key, as a JWK. */
export interface PublicJwk {
  kty: "EC";
  crv: "P-256";
  x: string;
  y: string;
}

/** The user's private keys, encrypted with a key derived from their recovery code. */
export interface E2eeKeyBackup {
  v: 1;
  kdf: "PBKDF2-SHA256";
  iterations: number;
  salt: string;
  iv: string;
  ct: string;
}

/** A conversation key sealed for one member (ephemeral ECDH + HKDF + AES-GCM), signed by its sender. */
export interface WrappedKey {
  v: 1;
  epk: PublicJwk;
  iv: string;
  ct: string;
  sig: string;
}

/** A message body (text and GIF) encrypted with a conversation key and signed by its author. */
export interface EncryptedPayload {
  v: 1;
  keyVersion: number;
  iv: string;
  ct: string;
  sig: string;
}

export interface E2eePublicKeys {
  userId: string;
  encryptionKey: PublicJwk;
  signingKey: PublicJwk;
  fingerprint: string;
  updatedAt: string;
}

export interface E2eeMyKeys extends E2eePublicKeys {
  backup: E2eeKeyBackup;
  /**
   * The same private keys sealed under the account password, so signing in
   * unlocks them. Null until set, and dropped whenever the password changes.
   */
  passwordBackup: E2eeKeyBackup | null;
  /** False while nobody has seen the recovery code (keys made automatically at sign-in). */
  recoveryConfirmed: boolean;
}

export interface ConversationEncryption {
  enabled: boolean;
  keyVersion: number;
  /** Membership or someone's keys changed: a new key must be distributed before the next message. */
  rekeyNeeded: boolean;
}

export interface ConversationKeyEnvelope {
  version: number;
  senderId: string | null;
  wrapped: WrappedKey;
}

/**
 * An image sent in an encrypted conversation. The file is encrypted on the
 * sender's device with its own random key and uploaded as an opaque blob; this
 * descriptor (key, hash, size and a thumbhash placeholder) travels inside the
 * signed message ciphertext, so only members can find, check and open it.
 */
export interface EncryptedAttachment {
  /** The uploaded ciphertext. Must be one of the message's media rows. */
  mediaId: string;
  /** AES-256-GCM key for this file alone, base64url. */
  key: string;
  iv: string;
  /** SHA-256 of the ciphertext, checked before decrypting. */
  sha256: string;
  mime: E2eeImageMime;
  /** Plaintext size in bytes. */
  size: number;
  width: number;
  height: number;
  filename?: string;
  /** ThumbHash of the image, shown blurred while the file downloads and decrypts. */
  placeholder?: string;
}

/** What gets encrypted inside an EncryptedPayload. */
export interface EncryptedBody {
  content: string;
  /** Validated on decrypt with gifAttachmentSchema and isGifMediaUrl, like the server does for plaintext GIFs. */
  gif?: GifAttachment;
  /** Validated on decrypt with encryptedAttachmentSchema. */
  attachments?: EncryptedAttachment[];
}

/** Image types an encrypted attachment may claim (what it's rendered as once decrypted). */
export const E2EE_IMAGE_MIMES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"] as const;
export type E2eeImageMime = (typeof E2EE_IMAGE_MIMES)[number];

/** Content type the server stores encrypted uploads under. It can't tell what's inside. */
export const ENCRYPTED_MEDIA_MIME = "application/x-atrium-encrypted";

/** Most encrypted attachments one message can carry. */
export const E2EE_MAX_ATTACHMENTS = 10;

/** Lowest PBKDF2 work factor the server accepts for key backups. */
export const E2EE_MIN_ITERATIONS = 600_000;

/** Canonical string both sides hash for a key fingerprint. */
export const fingerprintInput = (enc: PublicJwk, sig: PublicJwk) => `atrium-e2ee-v1|${enc.x}|${enc.y}|${sig.x}|${sig.y}`;

const b64url = (max: number) => z.string().min(1).max(max).regex(/^[A-Za-z0-9_-]+$/, "Invalid encoding");

export const publicJwkSchema = z
  .object({ kty: z.literal("EC"), crv: z.literal("P-256"), x: b64url(64), y: b64url(64) })
  .strip();

export const keyBackupSchema = z
  .object({
    v: z.literal(1),
    kdf: z.literal("PBKDF2-SHA256"),
    iterations: z.number().int().min(E2EE_MIN_ITERATIONS).max(10_000_000),
    salt: b64url(64),
    iv: b64url(32),
    ct: b64url(4096),
  })
  .strip();

export const wrappedKeySchema = z
  .object({ v: z.literal(1), epk: publicJwkSchema, iv: b64url(32), ct: b64url(256), sig: b64url(200) })
  .strip();

export const encryptedPayloadSchema = z
  // Room for the longest message plus a full set of attachment descriptors.
  .object({ v: z.literal(1), keyVersion: z.number().int().min(1), iv: b64url(32), ct: b64url(24_000), sig: b64url(200) })
  .strip();

export const encryptedAttachmentSchema = z
  .object({
    mediaId: z.string().uuid(),
    key: b64url(64),
    iv: b64url(32),
    sha256: b64url(64),
    mime: z.enum(E2EE_IMAGE_MIMES),
    size: z.number().int().min(1).max(200 * 1024 * 1024),
    width: z.number().int().min(1).max(16_384),
    height: z.number().int().min(1).max(16_384),
    filename: z.string().max(200).optional(),
    placeholder: b64url(100).optional(),
  })
  .strip();

export const setupE2eeKeysSchema = z.object({
  encryptionKey: publicJwkSchema,
  signingKey: publicJwkSchema,
  backup: keyBackupSchema,
  /** The private keys sealed under the account password (see E2eeMyKeys.passwordBackup). */
  passwordBackup: keyBackupSchema.optional(),
  /** False when the keys were made automatically and nobody was shown the recovery code. */
  recoveryConfirmed: z.boolean().default(true),
  /** Replace existing keys (a reset): history encrypted to the old keys becomes unreadable. */
  replace: z.boolean().default(false),
  /** The account password: a stolen session alone can't publish keys other people will encrypt to. */
  password: z.string().min(1).max(256),
});

export const updateKeyBackupSchema = z.object({ backup: keyBackupSchema });

export const updatePasswordBackupSchema = z.object({ backup: keyBackupSchema, password: z.string().min(1).max(256) });

export const distributeConversationKeySchema = z.object({
  version: z.number().int().min(1),
  keys: z
    .array(z.object({ userId: z.string().uuid(), wrapped: wrappedKeySchema }))
    .min(1)
    .max(10),
});

export const editDirectMessageSchema = z
  .object({ content: z.string().trim().max(2000).optional(), encrypted: encryptedPayloadSchema.optional() })
  .refine((v) => (v.encrypted ? !v.content : !!v.content), { message: "Send either text or an encrypted message" });
